import React, { useState } from 'react';
import { AlertTriangleIcon, SparklesIcon } from 'lucide-react';
import { ViewToggle, type ViewMode } from '../components/ViewToggle';
import { LoanModal } from '../components/LoanModal';
import { loans, loanStages, type Loan } from '../data/loanData';
import { formatCurrency } from '../data/mockData';
function aiReviewCount(loan: Loan): number {
  return loan.conditions.filter(condition => condition.status === 'AI Matched').length;
}
function LoanCard({
  loan,
  onOpen
}: {
  loan: Loan;
  onOpen: (loan: Loan) => void;
}) {
  const reviews = aiReviewCount(loan);
  return <button type="button" onClick={() => onOpen(loan)} className="w-full rounded-xl border border-gray-200 bg-white p-3.5 text-left transition-shadow hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-brand/30" data-id="element-309" data-mp="2a5znurocgby6">
      <div className="flex items-start gap-2" data-id="element-310" data-mp="2j7qv40yj8qu5">
        <div className="min-w-0" data-id="element-311" data-mp="2o5ikoobpg0v6">
          <p className="truncate text-[13px] font-semibold text-ink" data-id="element-312" data-mp="2o6k7fq232bsq">{loan.borrower}</p>
          <p className="text-[11.5px] text-gray-500" data-id="element-313" data-mp="1i4j5moscqeg4">
            {loan.number} · {loan.state}
          </p>
        </div>
        {loan.stalled && <AlertTriangleIcon className="ml-auto h-3.5 w-3.5 shrink-0 text-amber-500" aria-label="Stalled" data-id="element-314" data-mp="1e4s9s6nyx84q" />}
      </div>
      <p className="mt-2 text-[13px] font-medium text-ink" data-id="element-315" data-mp="145jhdi8r41kh">{formatCurrency(loan.amount)}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-316" data-mp="21r8gd4wi63c9">
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600" data-id="element-317" data-mp="1wt6wwiv60209">
          {loan.daysInStage}d in stage
        </span>
        {reviews > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-brand-soft px-2 py-0.5 text-[11px] font-medium text-brand-dark" data-id="element-318" data-mp="1s0m3vjk31sxs">
            <SparklesIcon className="h-3 w-3" aria-hidden="true" data-id="element-319" data-mp="2kihu5qdb3e9n" />
            {reviews} to review
          </span>}
      </div>
    </button>;
}
export function Loans() {
  const [view, setView] = useState<ViewMode>('list');
  const [selected, setSelected] = useState<Loan | null>(null);
  return <div className="space-y-4" data-id="element-320" data-mp="hwm4qi8i6u52">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-321" data-mp="hvktodrhm7nc">
        <div data-id="element-322" data-mp="45hci17gt44r">
          <h1 className="text-[22px] font-bold text-ink" data-id="element-323" data-mp="43wj4ewvzr8h">Loans in process</h1>
          <p className="mt-1 text-[13px] text-gray-500" data-id="element-324" data-mp="9397j43ep6d6">
            {loans.length} active files ·{' '}
            <span className="font-medium text-amber-600" data-id="element-325" data-mp="3qbmmoje14mf2">
              {loans.filter(loan => loan.stalled).length} stalled
            </span>{' '}
            · leads are kept in their own list
          </p>
        </div>
        <ViewToggle value={view} onChange={setView} data-id="element-326" data-mp="3hcj198re3uxe" />
      </div>

      {view === 'list' ? <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white" data-id="element-327" data-mp="3mb3vmrjynkdx">
          <table className="w-full min-w-[720px] text-left" data-id="element-328" data-mp="1fkw933vrhtzc">
            <thead className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500" data-id="element-329" data-mp="1kixq7j9mnj86">
              <tr data-id="element-330" data-mp="1umcvsadx0hvb">
                <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-331" data-mp="1unxorvkkzsr2">Borrower</th>
                <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-332" data-mp="1pokregw333a8">Loan</th>
                <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-333" data-mp="23exzqkcw0k03">Stage</th>
                <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-334" data-mp="23h109ybo1vj2">Processor</th>
                <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-335" data-mp="1yn6jb2ng3uno">Conditions</th>
                <th scope="col" className="px-5 py-2.5 font-semibold" data-id="element-336" data-mp="2dbsvivhs06ao">Closing</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-[13px]" data-id="element-337" data-mp="28gdmcgupqykl">
              {loans.map(loan => <tr key={loan.id} onClick={() => setSelected(loan)} className="cursor-pointer hover:bg-gray-50" data-id="element-338" data-mp="2mbpsdkebkbgt">
                  <td className="px-5 py-3" data-id="element-339" data-mp="s0axkp6gblu7">
                    <div className="flex items-center gap-2 font-semibold text-ink" data-id="element-340" data-mp="nyxqycvjbem8">
                      {loan.borrower}
                      {loan.stalled && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10.5px] font-medium text-amber-700" data-id="element-341" data-mp="phxrp8edx7a1">
                          Stalled {loan.daysInStage}d
                        </span>}
                    </div>
                    <div className="text-[12px] text-gray-500" data-id="element-342" data-mp="13eb7md39t0me">
                      {loan.number} · {loan.state}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-ink" data-id="element-343" data-mp="aqfgrp0du2xj">{formatCurrency(loan.amount)}</td>
                  <td className="px-5 py-3 text-gray-600" data-id="element-344" data-mp="fps388ncd7zl">{loan.stage}</td>
                  <td className="px-5 py-3 text-gray-600" data-id="element-345" data-mp="fogzu9xt6290">{loan.processor}</td>
                  <td className="px-5 py-3" data-id="element-346" data-mp="kha3svlgd6k1">
                    {aiReviewCount(loan) > 0 ? <span className="inline-flex items-center gap-1 rounded-full bg-brand-soft px-2 py-0.5 text-[11.5px] font-medium text-brand-dark" data-id="element-347" data-mp="3j5qu0n9mvzx6">
                        <SparklesIcon className="h-3 w-3" aria-hidden="true" data-id="element-348" data-mp="3glu4r70m6u70" />
                        {aiReviewCount(loan)} to review
                      </span> : <span className="text-[12px] text-gray-500" data-id="element-349" data-mp="36nwhomuhr3fz">
                        {loan.conditions.filter(c => c.status === 'Outstanding').length}{' '}
                        outstanding
                      </span>}
                  </td>
                  <td className="px-5 py-3 text-gray-600" data-id="element-350" data-mp="2xoj5hmezg0rk">{loan.closingDate}</td>
                </tr>)}
            </tbody>
          </table>
        </div> : <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5" data-id="element-351" data-mp="2ny6ou5aagrqp">
          {loanStages.map(stage => {
        const items = loans.filter(loan => loan.stage === stage);
        return <section key={stage} className="rounded-xl bg-gray-100/70 p-2.5" data-id="element-352" data-mp="2ew787e36rpoa">
                <header className="flex items-center justify-between px-1 pb-2" data-id="element-353" data-mp="29yoy3kdfn4ep">
                  <h2 className="text-[12px] font-semibold text-ink" data-id="element-354" data-mp="209rzh21sznon">{stage}</h2>
                  <span className="text-[11.5px] text-gray-500" data-id="element-355" data-mp="24bda0gqv7pvo">{items.length}</span>
                </header>
                <div className="space-y-2" data-id="element-356" data-mp="1qcdi90822uva">
                  {items.map(loan => <LoanCard key={loan.id} loan={loan} onOpen={setSelected} data-id="element-357" data-mp="1vaot9ea37bf3" />)}
                  {items.length === 0 && <p className="rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-[11.5px] text-gray-400" data-id="element-358" data-mp="2j1ubu17ewht3">
                      Empty
                    </p>}
                </div>
              </section>;
      })}
        </div>}

      <LoanModal loan={selected} onClose={() => setSelected(null)} data-id="element-359" data-mp="2e8hwyisjbpdj" />
    </div>;
}